import {AdminAlert} from '../../components/AdminAlert/AdminAlert'
import axios from 'axios'
import React, {useEffect, useMemo, useState} from 'react'
import {Modal} from 'react-bootstrap'
import LoadingOverlay from 'react-loading-overlay'
import {AdminDataTable, AdminStatusBadge, AdminTableAction, AdminTableActions, AdminTableSearch} from '../../components/AdminDataTable/AdminDataTable'
import {useAuth} from '../../modules/auth'
import {PasswordSecurityFields, validatePasswordFields, passwordPayload} from '../../components/ChangePasswordForm/ChangePasswordForm'
import '../Distribuidores/Distribuidores.css'
import './Vendedores.css'

const emptyForm = {clave: '', nombre: '', apellido: '', correo: '', telefono: '', estatus: 'Activo', password: '', confirmPassword: ''}
function Vendedores() {
  const API_URL = process.env.REACT_APP_API_URL
  const {currentUser} = useAuth()
  const [sellers, setSellers] = useState([])
  const [search, setSearch] = useState('')
  const [loading, setLoading] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [editing, setEditing] = useState(null)
  const [form, setForm] = useState(emptyForm)
  const [message, setMessage] = useState(null)
  const [formError, setFormError] = useState('')

  const loadSellers = async () => {
    setLoading(true)
    try {
      const {data} = await axios.get(`${API_URL}/vendedores-admin`)
      setSellers(data.data || [])
    } catch (error) { setMessage({type: 'danger', text: error.response?.data?.message || 'No fue posible consultar los vendedores.'}) }
    finally { setLoading(false) }
  }

  useEffect(() => {
    loadSellers()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const filtered = useMemo(() => {
    const term = search.trim().toLocaleLowerCase('es-MX')
    if (!term) return sellers
    return sellers.filter((seller) => `${seller.clave || ''} ${seller.nombreCompleto} ${seller.correo} ${seller.telefono || ''} ${seller.estatus}`.toLocaleLowerCase('es-MX').includes(term))
  }, [sellers, search])

  const openCreate = () => { setEditing(null); setForm(emptyForm); setFormError(''); setShowModal(true) }
  const openEdit = (seller) => {
    setEditing(seller)
    setForm({clave: seller.clave || '', nombre: seller.nombre || '', apellido: seller.apellido || '', correo: seller.correo || '', telefono: seller.telefono || '', estatus: ['Activo', 'Inactivo'].includes(seller.estatus) ? seller.estatus : 'Inactivo', password: '', confirmPassword: ''})
    setFormError(''); setShowModal(true)
  }

  const validate = () => {
    if (!form.clave.trim()) return 'La clave es obligatoria.'
    if (!form.nombre.trim()) return 'El nombre es obligatorio.'
    if (!/^\S+@\S+\.\S+$/.test(form.correo.trim())) return 'Ingresa un correo electrónico válido.'
    return validatePasswordFields(form.password, form.confirmPassword, !editing)
  }

  const save = async (event) => {
    event.preventDefault()
    const error = validate()
    if (error) { setFormError(error); return }
    setLoading(true); setFormError('')
    try {
      const {password, confirmPassword, ...generalFields} = form
      const payload = {...generalFields, clave: form.clave.trim(), nombre: form.nombre.trim(), apellido: form.apellido.trim(), correo: form.correo.trim(), telefono: form.telefono.trim()}
      if (editing) {
        await axios.put(`${API_URL}/vendedores-admin/${editing.id}`, {...payload, ...passwordPayload(password, confirmPassword)})
      } else await axios.post(`${API_URL}/vendedores-admin`, {...payload, password, confirmPassword})
      await loadSellers(); setShowModal(false)
      setForm(emptyForm)
      setMessage({type: 'success', text: editing ? 'Vendedor actualizado correctamente.' : 'Vendedor creado correctamente.'})
    } catch (requestError) {
      console.error('No fue posible guardar el vendedor.', requestError)
      setFormError(requestError.response?.data?.message || 'No fue posible guardar el vendedor.')
    }
    finally { setLoading(false) }
  }

  const remove = async (seller) => {
    if (!window.confirm('¿Seguro que deseas eliminar este vendedor?')) return
    setLoading(true)
    try {
      const {data} = await axios.delete(`${API_URL}/vendedores-admin/${seller.id}`)
      await loadSellers(); setMessage({type: 'success', text: data.message})
    } catch (error) { setMessage({type: 'danger', text: error.response?.data?.message || 'No fue posible eliminar el vendedor.'}) }
    finally { setLoading(false) }
  }

  const columns = [
    {name: 'Clave', selector: (row) => row.clave || '', sortable: true, cell: (row) => <strong className='seller-key'>{row.clave || 'No disponible'}</strong>},
    {name: 'Nombre', selector: (row) => row.nombreCompleto, sortable: true, grow: 1.4},
    {name: 'Correo', selector: (row) => row.correo, sortable: true, grow: 1.5, wrap: true},
    {name: 'Teléfono', selector: (row) => row.telefono || '', sortable: true, cell: (row) => row.telefono || '—', wrap: true},
    {name: 'Estatus', selector: (row) => row.estatus, sortable: true, cell: (row) => <AdminStatusBadge status={row.estatus} />},
    {name: 'Acciones', right: true, width: '140px', cell: (row) => <AdminTableActions><AdminTableAction variant='edit' title='Editar' icon='fas fa-pen' onClick={() => openEdit(row)} />{currentUser?.rol === 'Admin' && <AdminTableAction variant='delete' title='Eliminar' icon='fas fa-trash' onClick={() => remove(row)} />}</AdminTableActions>},
  ]

  return <LoadingOverlay active={loading} spinner text='Cargando...'><div className='sellers-page'>
    <AdminAlert message={message} onClose={setMessage} />
    <div className='card card-custom shadow admin-list-card sellers-card'><div className='card-header sellers-header'><div><h1>Vendedores</h1><p>Administra las cuentas que pueden acceder al Portal Vendedor y asignarse a distribuidores.</p></div><button type='button' className='btn btn-primary sellers-add' onClick={openCreate}><i className='fas fa-plus'></i> Agregar vendedor</button></div>
      <div className='card-body'><AdminTableSearch value={search} onChange={(event) => setSearch(event.target.value)} ariaLabel='Buscar vendedores' />
        <AdminDataTable columns={columns} data={filtered} pagination paginationPerPage={10} paginationRowsPerPageOptions={[10,25,50,100]} noDataComponent='No se encontraron vendedores.' />
      </div>
    </div>
    <Modal show={showModal} onHide={() => setShowModal(false)} centered dialogClassName='seller-modal'><form onSubmit={save}><Modal.Header closeButton><Modal.Title>{editing ? 'Editar vendedor' : 'Agregar vendedor'}</Modal.Title></Modal.Header><Modal.Body>
      {formError && <div className='alert alert-danger seller-form-error'>{formError}</div>}
      <section className='distributor-section seller-modal-section'>
        <h3>Información general del vendedor</h3>
        <div className='distributor-grid seller-general-grid'>
          <label className='distributor-field'><span>Clave</span><input className='form-control' maxLength='20' required value={form.clave} onChange={(event) => setForm({...form, clave: event.target.value})} /></label>
          <label className='distributor-field'><span>Nombre</span><input className='form-control' maxLength='100' required value={form.nombre} onChange={(event) => setForm({...form, nombre: event.target.value})} /></label>
          <label className='distributor-field'><span>Apellidos</span><input className='form-control' maxLength='100' value={form.apellido} onChange={(event) => setForm({...form, apellido: event.target.value})} /></label>
          <label className='distributor-field seller-email-field'><span>Correo electrónico</span><input className='form-control' type='email' maxLength='254' required value={form.correo} onChange={(event) => setForm({...form, correo: event.target.value})} /></label>
          <label className='distributor-field'><span>Teléfono</span><input className='form-control' type='tel' maxLength='40' value={form.telefono} onChange={(event) => setForm({...form, telefono: event.target.value})} /></label>
          <label className='distributor-field'><span>Estatus</span><select className='form-select' value={form.estatus} onChange={(event) => setForm({...form, estatus: event.target.value})}><option>Activo</option><option>Inactivo</option></select></label>
        </div>
      </section>
      <section className='distributor-section seller-modal-section'>
        <h3>Contraseña y seguridad</h3>
        <div className='password-security-fields'>
          <PasswordSecurityFields required={!editing} newPassword={form.password} confirmPassword={form.confirmPassword} onNewPasswordChange={(password) => {setForm({...form, password}); setFormError('')}} onConfirmPasswordChange={(confirmPassword) => {setForm({...form, confirmPassword}); setFormError('')}} />
        </div>
      </section>
      <div className='distributor-actions seller-modal-actions'><button type='submit' className='btn btn-primary'>{editing ? 'Guardar cambios' : 'Guardar'}</button><button type='button' className='btn btn-secondary' onClick={() => setShowModal(false)}>Cancelar</button></div>
    </Modal.Body></form></Modal>
  </div></LoadingOverlay>
}

export {Vendedores}
